import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type PointerEvent,
} from 'react';
import styles from '@/components/ToolsPage/ToolsPage.module.css';
import {
  CallToolIcon,
  GetOffsetIcon,
  ToolReleaseIcon,
  EntryIcon,
  StatusCheckIcon,
  StatusNotReadyIcon,
  DefineToolIcon,
  RemoveToolIcon,
} from '@/icons';
import { GCodePanel } from '@/components/GCodePanel/GCodePanel';
import {
  useSendCommand,
  useSetToolInSpindle,
  useSetToolCatalog,
  useActiveTool,
  useToolTableVersion,
  useMachineStore,
} from '@/store/store';
import { ToolFormModal } from '@/components/ToolsPage/ToolFormModal';
import { ToolActionModal } from '@/components/ToolsPage/ToolActionModal';
import { RemoveToolModal } from '@/components/ToolsPage/RemoveToolModal';
import {
  TOOL_ART,
  TOOL_NUMBERS,
  largeArt,
  emptySlot,
  isEmpty,
  isMeasured,
  fetchTools,
  persistTools,
  numericPart,
  toolNumberOf,
  buildToolCatalog,
  type ToolRow,
} from '@/components/ToolsPage/toolData';

// Tool Release is a hold-to-activate control: the spindle only unclamps (M506)
// after the button is held for this long, so a stray tap can't drop the tool.
const RELEASE_HOLD_MS = 1500;

type MenuAction = 'edit' | 'define' | 'remove';

// Fixed-position context menu, right-anchored under the clicked three-dots button.
// Width lives in CSS (.tool-menu); we anchor by `right` so no width math is needed.
interface MenuState {
  no: string;
  top: number;
  right: number;
}

interface ModalState {
  mode: MenuAction;
  tool: ToolRow;
}

function OffsetStatus({
  measured,
  label = false,
}: {
  measured: boolean;
  label?: boolean;
}): JSX.Element {
  const Icon = measured ? StatusCheckIcon : StatusNotReadyIcon;
  const text = measured ? 'Measured' : 'Not measured';
  return (
    <span className={`${styles.offsetStatus} ${measured ? styles.offsetOk : styles.offsetNone}`}>
      <Icon className={styles.offsetIcon} aria-label={label ? undefined : text} />
      {label && text}
    </span>
  );
}

function ThreeDots({
  onOpen,
}: {
  onOpen: (e: MouseEvent<HTMLButtonElement>) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      className={styles.threeDots}
      aria-label="Tool options"
      aria-haspopup="menu"
      onClick={onOpen}
    >
      <span />
      <span />
      <span />
    </button>
  );
}

export function ToolsPage(): JSX.Element {
  const [tools, setTools] = useState<ToolRow[]>(() => TOOL_NUMBERS.map(emptySlot));
  // Default to the last-called tool so re-entering the tab keeps that selection
  // (read once at mount; manual selections afterwards aren't overridden).
  const activeTool = useActiveTool();
  const [selectedNo, setSelectedNo] = useState(activeTool?.no ?? 'T01');
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [modal, setModal] = useState<ModalState | null>(null);
  // Loading modal shown while a Call Tool / Get Offset op runs on the machine.
  const [loading, setLoading] = useState<{
    action: 'call' | 'offset';
    no: string;
    type: string;
  } | null>(null);
  const sendCommand = useSendCommand();
  const setToolInSpindle = useSetToolInSpindle();
  const setToolCatalog = useSetToolCatalog();

  // Tool Release hold state: `releaseHolding` drives the fill animation while
  // pressed; `releaseHeld` means M506 already fired (spindle unclamped), so the
  // pointer-up will send the matching M507 (clamp).
  const [releaseHolding, setReleaseHolding] = useState(false);
  const [releaseHeld, setReleaseHeld] = useState(false);
  const releaseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Version the last fetch acted on (null until the first load). `mounted` drops
  // any in-flight fetch result after unmount.
  const loadedVersionRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Read the table from the backend. The JSON can also be written from outside
  // (measurement flow). `measuredAt` isn't persisted, so carry it across
  // refreshes locally.
  const load = useCallback((): void => {
    fetchTools()
      .then((rows) => {
        if (!mountedRef.current) return;
        setTools((prev) =>
          rows.map((row) => {
            if (row.offsetStatus !== 'measured') return row;
            const before = prev.find((p) => p.no === row.no);
            return before?.measuredAt ? { ...row, measuredAt: before.measuredAt } : row;
          }),
        );
        // Keep the store's tool geometry cache fresh so the active tool's
        // spec (resolved from the machine's spindle number) stays correct.
        setToolCatalog(buildToolCatalog(rows));
      })
      .catch(() => {
        // Transient backend hiccup — keep the last-known table on screen.
      });
  }, [setToolCatalog]);

  // Re-fetch when the backend reports a new tool-table file version. On mount
  // the version is '' and the first STATUS frame flips it to the real value,
  // which drives the initial load. While a modal is open we defer — the effect
  // re-runs on close (`modal` is a dep) and the version guard then catches up,
  // so an external change can't clobber an open edit form.
  const toolTableVersion = useToolTableVersion();
  useEffect(() => {
    if (modal !== null) return;
    if (toolTableVersion === loadedVersionRef.current) return;
    loadedVersionRef.current = toolTableVersion;
    load();
  }, [toolTableVersion, modal, load]);

  // Close the loading modal once the machine has actually started and finished
  // the op. We wait for a busy→idle transition (not just idle) so an op that
  // hasn't registered yet doesn't dismiss the modal instantly; `sawBusyRef` is
  // reset in the open handlers. A fallback timeout covers ops that never flip
  // busy (e.g. M501 completing too fast to observe). Driven off the store's busy
  // stream via subscribe so setState runs in the external-subscription callback,
  // not synchronously in an effect body.
  const sawBusyRef = useRef(false);
  const fallbackRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return useMachineStore.subscribe((state, prev) => {
      if (state.mdiBusy && !prev.mdiBusy) {
        sawBusyRef.current = true;
        // A real op is running — trust the busy→idle transition and cancel the
        // "never registered busy" fallback so a long tool change isn't cut off.
        if (fallbackRef.current) {
          clearTimeout(fallbackRef.current);
          fallbackRef.current = null;
        }
        return;
      }
      if (!state.mdiBusy && prev.mdiBusy && sawBusyRef.current) {
        setLoading(null);
      }
    });
  }, []);

  useEffect(() => {
    if (!loading) return;
    fallbackRef.current = setTimeout(() => setLoading(null), 8000);
    return () => {
      if (fallbackRef.current) clearTimeout(fallbackRef.current);
      fallbackRef.current = null;
    };
  }, [loading]);

  const selectedTool = tools.find((t) => t.no === selectedNo) ?? tools[0];
  // Actions target the selected slot; an empty (undefined) pocket has nothing to
  // call, offset or release, so all three actions are disabled for it.
  const noTool = !selectedTool || isEmpty(selectedTool);
  const menuRow = menu ? tools.find((t) => t.no === menu.no) : undefined;
  const menuRowEmpty = menuRow ? isEmpty(menuRow) : false;

  // Removal keeps the pocket but blanks it back to an empty slot.
  const handleRemove = (no: string): void => {
    setTools((prev) => {
      const next = prev.map((t) => (t.no === no ? emptySlot(no) : t));
      void persistTools(next);
      return next;
    });
    setModal(null);
  };

  const handleSave = (saved: ToolRow): void => {
    setTools((prev) => {
      const next = prev.map((t) => (t.no === saved.no ? saved : t));
      void persistTools(next);
      return next;
    });
    // Push the geometry to LinuxCNC's tool table. G10 L1's R word is a RADIUS, so
    // the UI diameter is halved; only include words we actually have values for.
    const toolNum = parseInt(saved.no.replace(/\D/g, ''), 10);
    const dia = parseFloat(numericPart(saved.diameter));
    const len = parseFloat(numericPart(saved.toolLength ?? ''));
    const words = [`G10 L1 P${toolNum.toString()}`];
    if (!Number.isNaN(dia)) words.push(`R${(dia / 2).toFixed(4)}`);
    if (!Number.isNaN(len)) words.push(`Z${len.toFixed(4)}`);
    if (words.length > 1) sendCommand({ type: 'MDI', command: words.join(' ') });
  };

  // Call Tool — select + change to the highlighted tool via MDI (e.g. "T05 M6").
  const handleCallTool = (): void => {
    if (!selectedTool) return;
    sendCommand({ type: 'MDI', command: `${selectedTool.no} M6` });
    sawBusyRef.current = false;
    setLoading({ action: 'call', no: selectedTool.no, type: selectedTool.type });
    // Optimistically set the spindle number so the badge/preview reflect it
    // immediately; the machine's STATUS confirms (or corrects) it. Geometry is
    // resolved from the catalog, so no need to pass type/diameter here.
    setToolInSpindle(toolNumberOf(selectedTool.no));
  };

  // Get Offset — push the tool's length via "M501 K<len>" and mark it measured.
  // M501 writes the offset for whatever tool is in the spindle, so this is only
  // valid for the active tool: guard that the selected row IS the spindle tool.
  const handleGetOffset = (): void => {
    if (!selectedTool || activeTool === null || selectedTool.no !== activeTool.no) return;
    const length = numericPart(selectedTool.toolLength ?? '');
    if (!length) return;
    sendCommand({ type: 'MDI', command: `M501 K${length}` });
    sawBusyRef.current = false;
    setLoading({ action: 'offset', no: selectedTool.no, type: selectedTool.type });
    const measuredAt = new Date().toISOString();
    setTools((prev) => {
      const next = prev.map((t) =>
        t.no === selectedTool.no ? { ...t, offsetStatus: 'measured' as const, measuredAt } : t,
      );
      void persistTools(next);
      return next;
    });
  };

  // Clear a pending Tool Release hold timer on unmount. If the tool is left
  // unclamped (very rare: navigated away mid-hold), the operator clamps it back
  // manually via the MDI panel — no automatic M507 here.
  useEffect(
    () => () => {
      if (releaseTimerRef.current) clearTimeout(releaseTimerRef.current);
    },
    [],
  );

  // Press-and-hold: start the fill and arm the 1.5s timer that sends M506.
  // Capture the pointer so a finger that slides off the button still reports its
  // release here — clamping (M507) must only fire on an actual lift, not on the
  // finger drifting outside the button while held.
  const handleReleasePressStart = (e: PointerEvent<HTMLButtonElement>): void => {
    if (releaseHeld || releaseTimerRef.current) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setReleaseHolding(true);
    releaseTimerRef.current = setTimeout(() => {
      releaseTimerRef.current = null;
      setReleaseHolding(false);
      setReleaseHeld(true);
      sendCommand({ type: 'MDI', command: 'M506' });
    }, RELEASE_HOLD_MS);
  };

  // Pointer up/cancel: cancel an unfinished hold (no command), or if the tool was
  // released (M506 sent), clamp it back with M507.
  const handleReleasePressEnd = (): void => {
    if (releaseTimerRef.current) {
      clearTimeout(releaseTimerRef.current);
      releaseTimerRef.current = null;
    }
    setReleaseHolding(false);
    if (releaseHeld) {
      setReleaseHeld(false);
      sendCommand({ type: 'MDI', command: 'M507' });
    }
  };

  // Stop button on the loading modal — abort the in-flight MDI op and dismiss.
  const handleStopLoading = (): void => {
    sendCommand({ type: 'MDI_ABORT' });
    setLoading(null);
  };

  const openMenu = (e: MouseEvent<HTMLButtonElement>, no: string): void => {
    // Keep the row-select click from firing; the dots are their own control.
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setMenu({ no, top: rect.bottom + 6, right: window.innerWidth - rect.right });
  };

  const openModal = (mode: MenuAction): void => {
    if (!menu) return;
    const tool = tools.find((t) => t.no === menu.no) ?? tools[0];
    setMenu(null);
    if (tool) setModal({ mode, tool });
  };

  return (
    <div className={styles.page}>
      {/* Tool table */}
      <section className={styles.tableCard}>
        <div className={styles.tableHead}>
          <span className={styles.colNo}>T No</span>
          <div className={styles.toolGroup}>
            <span className={styles.colTool}>Tool</span>
            <div className={styles.valueGroup}>
              <span className={styles.colType}>Tool Type</span>
              <span className={styles.colVal}>Ø (mm)</span>
              <span className={styles.colLen}>Tool L. (mm)</span>
              <span className={styles.colHelix}>Helix L. (mm)</span>
              <span className={styles.colFlute}>Flutes</span>
              <span className={styles.colStatus}>Offset Status</span>
            </div>
          </div>
        </div>

        <div className={styles.tableBody}>
          {tools.map((tool) => (
            <button
              key={tool.no}
              type="button"
              className={`${styles.row} ${selectedNo === tool.no ? styles.rowActive : ''}`}
              onClick={() => setSelectedNo(tool.no)}
            >
              <span className={styles.colNo}>{tool.no}</span>
              <div className={styles.toolGroup}>
                <span className={styles.colTool}>
                  {tool.type !== '' && (
                    <img src={TOOL_ART[tool.type].thumb} alt="" className={styles.thumb} />
                  )}
                </span>
                <div className={styles.valueGroup}>
                  <span className={styles.colType}>{tool.type}</span>
                  <span className={styles.colVal}>{isEmpty(tool) ? '' : tool.diameter || '-'}</span>
                  <span className={styles.colLen}>
                    {isEmpty(tool) ? '' : tool.toolLength || '-'}
                  </span>
                  <span className={styles.colHelix}>{isEmpty(tool) ? '' : tool.helix || '-'}</span>
                  <span className={styles.colFlute}>{isEmpty(tool) ? '' : tool.flute || '-'}</span>
                  <span className={styles.colStatus}>
                    <OffsetStatus measured={isMeasured(tool)} />
                    <ThreeDots onOpen={(e) => openMenu(e, tool.no)} />
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Lower grid: selected tool + actions/measurement */}
      <div className={styles.lowerGrid}>
        {/* Selected tool */}
        <section className={styles.panel}>
          <h2 className={styles.panelTitle}>Selected Tool</h2>
          <div className={styles.divider} />
          <div className={styles.selectedBody}>
            <div className={styles.selectedThumb}>
              <span className={styles.thumbGlow} />
              {selectedTool && selectedTool.type !== '' && (
                <img
                  src={largeArt(selectedTool.type)}
                  alt={selectedTool.type}
                  className={styles.selectedThumbIcon}
                />
              )}
            </div>
            <div className={styles.selectedInfo}>
              <div className={styles.selectedHeading}>
                <span className={styles.selectedNo}>{selectedTool?.no}</span>
                <span className={styles.selectedType}>{selectedTool?.type}</span>
              </div>
              <dl className={styles.specList}>
                <div className={styles.specRow}>
                  <dt>Diameter</dt>
                  <dd>{selectedTool?.diameter}</dd>
                </div>
                <div className={styles.specRow}>
                  <dt>Helix Length</dt>
                  <dd>{selectedTool?.helix}</dd>
                </div>
                <div className={styles.specRow}>
                  <dt>Flute Count</dt>
                  <dd>{selectedTool?.flute}</dd>
                </div>
                <div className={styles.specRow}>
                  <dt>Holder Type</dt>
                  <dd>ISO020</dd>
                </div>
                <div className={`${styles.specColumn} ${styles.specDivider}`}>
                  <dt>Tool Offset (Length)</dt>
                  <dd className={styles.specHighlight}>{selectedTool?.toolLength || '—'}</dd>
                </div>
                <div className={styles.specColumn}>
                  <dt>Offset status</dt>
                  <dd>
                    <OffsetStatus
                      measured={selectedTool ? isMeasured(selectedTool) : false}
                      label
                    />
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </section>

        <div className={styles.rightColumn}>
          {/* Tool actions */}
          <section className={`${styles.panel} ${styles.panelActions}`}>
            <h2 className={styles.panelTitle}>Tool Actions</h2>
            <div className={styles.divider} />
            <p className={styles.actionsNote}>
              <span className={styles.actionsNoteMuted}>Actions apply to selected tool</span> :{' '}
              {selectedTool?.no}
            </p>
            <div className={styles.actionButtons}>
              <button
                type="button"
                className={`${styles.actionBtn} ${styles.actionBtnPrimary}`}
                onClick={handleCallTool}
              >
                <CallToolIcon className={styles.actionIcon} />
                Call Tool
              </button>
              <button
                type="button"
                className={styles.actionBtn}
                onClick={handleGetOffset}
                disabled={noTool || activeTool === null || selectedNo !== activeTool.no}
              >
                <GetOffsetIcon className={styles.actionIcon} />
                Get Offset
              </button>
              <button
                type="button"
                className={`${styles.actionBtn} ${styles.actionBtnRelease} ${
                  releaseHeld ? styles.actionBtnReleaseActive : ''
                }`}
                onPointerDown={handleReleasePressStart}
                onPointerUp={handleReleasePressEnd}
                onPointerCancel={handleReleasePressEnd}
                onContextMenu={(e) => e.preventDefault()}
              >
                <span
                  className={styles.holdFill}
                  data-holding={releaseHolding || undefined}
                  style={{ transitionDuration: releaseHolding ? `${RELEASE_HOLD_MS}ms` : '0ms' }}
                />
                <span className={styles.actionBtnLabel}>
                  <ToolReleaseIcon className={styles.actionIcon} />
                  {releaseHeld ? 'Release Active — let go to clamp' : 'Tool Release'}
                </span>
              </button>
            </div>
          </section>
        </div>
      </div>

      {/* Upload bar */}
      <GCodePanel />

      {/* Row context menu — a transparent backdrop catches the outside click. */}
      {menu && (
        <>
          <div className={styles.menuBackdrop} onClick={() => setMenu(null)} />
          <div className={styles.toolMenu} style={{ top: menu.top, right: menu.right }} role="menu">
            <button
              type="button"
              role="menuitem"
              className={styles.toolMenuItem}
              disabled={menuRowEmpty}
              onClick={() => openModal('edit')}
            >
              <EntryIcon className={styles.toolMenuIcon} />
              Edit Tool
            </button>
            <div className={styles.toolMenuDivider} />
            <button
              type="button"
              role="menuitem"
              className={`${styles.toolMenuItem} ${styles.toolMenuItemDefine}`}
              disabled={!menuRowEmpty}
              onClick={() => openModal('define')}
            >
              <DefineToolIcon className={styles.toolMenuIcon} />
              Define Tool
            </button>
            <div className={styles.toolMenuDivider} />
            <button
              type="button"
              role="menuitem"
              className={`${styles.toolMenuItem} ${styles.toolMenuItemRemove}`}
              disabled={menuRowEmpty}
              onClick={() => openModal('remove')}
            >
              <RemoveToolIcon className={styles.toolMenuIcon} />
              Remove Tool
            </button>
          </div>
        </>
      )}

      {/* Action modals */}
      {modal?.mode === 'remove' && (
        <RemoveToolModal
          tool={modal.tool}
          onConfirm={() => handleRemove(modal.tool.no)}
          onClose={() => setModal(null)}
        />
      )}
      {(modal?.mode === 'edit' || modal?.mode === 'define') && (
        <ToolFormModal
          mode={modal.mode}
          tool={modal.tool}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}

      {/* Loading modal — visible while a Call Tool / Get Offset op runs on the
          machine; closes when the backend mdiBusy flag clears. */}
      {loading && (
        <ToolActionModal
          action={loading.action}
          toolNo={loading.no}
          toolType={loading.type}
          onStop={handleStopLoading}
          onClose={() => setLoading(null)}
        />
      )}
    </div>
  );
}
